import { useState, useEffect, useRef } from "react";
import {
  Input,
  ALL_FORMATS,
  BlobSource,
  Output,
  BufferTarget,
  Mp3OutputFormat,
  canEncodeAudio,
  Conversion,
} from "mediabunny";
import { registerMp3Encoder } from "@mediabunny/mp3-encoder";

export type ConversionState =
  | { status: "idle" }
  | { status: "encoding" }
  | { status: "done"; url: string }
  | { status: "error"; message: string };

let encoderReady = false;

async function ensureEncoder() {
  if (encoderReady) return;
  if (!(await canEncodeAudio("mp3"))) registerMp3Encoder();
  encoderReady = true;
}

export function useConverter(file: File | null): ConversionState {
  const [state, setState] = useState<ConversionState>({ status: "idle" });
  const urlRef = useRef<string | null>(null);

  useEffect(() => {
    if (!file) {
      setState({ status: "idle" });
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        setState({ status: "encoding" });
        await ensureEncoder();
        if (cancelled) return;

        const input = new Input({
          source: new BlobSource(file),
          formats: ALL_FORMATS,
        });
        const output = new Output({
          format: new Mp3OutputFormat(),
          target: new BufferTarget(),
        });

        const conversion = await Conversion.init({ input, output });
        await conversion.execute();
        if (cancelled) return;

        if (urlRef.current) URL.revokeObjectURL(urlRef.current);
        const url = URL.createObjectURL(
          new Blob([output.target.buffer!], { type: "audio/mpeg" }),
        );
        urlRef.current = url;
        setState({ status: "done", url });
      } catch (err) {
        if (!cancelled)
          setState({
            status: "error",
            message: (err as Error).message ?? "Encoding failed",
          });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [file]);

  useEffect(() => {
    return () => {
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };
  }, []);

  return state;
}
